import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Phone, Calendar, Edit, Trash2, Eye, DollarSign } from 'lucide-react';
import { Employee } from '../../types/employee';
import { employeeService } from '../../services/employeeService';
import { contractService } from '../../services/contractService';
import { formatCurrency } from '../../utils/currencyUtils';
import { EditEmployeeModal } from './employee-components/EditEmployeeModal';

interface EventoFuncionario {
  contractId: string;
  nomeAniversariante: string;
  dataFesta: string;
  valorPagamento: number;
  funcao: string;
}

export const EmployeeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [eventos, setEventos] = useState<EventoFuncionario[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    const loadEmployee = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const employeeData = await employeeService.getEmployeeById(id);
        if (employeeData) {
          setEmployee(employeeData);
          await loadEventos(id);
        } else {
          console.error('Funcionário não encontrado');
          navigate('/admin/funcionarios');
        }
      } catch (error) {
        console.error('Erro ao carregar funcionário:', error);
        navigate('/admin/funcionarios');
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
  }, [id, navigate]);

  const loadEventos = async (employeeId: string) => {
    try {
      setLoadingEventos(true);
      const employeeContracts = await contractService.getContractsByEmployee(employeeId);
      setEventos(employeeContracts);
    } catch (error) {
      console.error('Erro ao carregar eventos do funcionário:', error);
    } finally {
      setLoadingEventos(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString.trim() === '') {
      return 'Data não informada';
    }
    
    try {
      let dateObj;
      if (typeof dateString === 'string') {
        if (dateString.includes('/')) {
          const [day, month, year] = dateString.split('/');
          dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          dateObj = new Date(dateString);
        }
      } else {
        dateObj = new Date(dateString);
      }
      
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toLocaleDateString('pt-BR');
      } else {
        return 'Data inválida';
      }
    } catch (error) {
      return 'Data inválida';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'inativo':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ativo':
        return '🟢 Ativo';
      case 'inativo':
        return '🔴 Inativo';
      default:
        return '⚪ Indefinido';
    }
  };

  const handleEdit = (updatedEmployee: Employee) => {
    setEmployee(updatedEmployee);
    setShowEditModal(false);
  };

  const handleDelete = async () => {
    if (!employee || !id) return;
    
    const confirmDelete = window.confirm(
      `Tem certeza que deseja excluir o funcionário "${employee.nome}"? Esta ação não pode ser desfeita.`
    );
    
    if (confirmDelete) {
      try {
        await employeeService.deleteEmployee(id);
        navigate('/admin/funcionarios');
      } catch (error) {
        console.error('Erro ao excluir funcionário:', error);
        alert('Erro ao excluir funcionário. Tente novamente.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando funcionário...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Funcionário não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/funcionarios')}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{employee.nome}</h1>
                <p className="text-sm text-slate-500">Detalhes do funcionário</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(employee.status)}`}>
                {getStatusLabel(employee.status)}
              </span>
              
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Editar
              </button>
              
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações Pessoais */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Informações Pessoais</h3>
                    <p className="text-sm text-teal-100">Dados do funcionário</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Nome Completo</p>
                    <p className="font-medium text-slate-900">{employee.nome}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-slate-400" />
                  <div>
                    <p className="text-sm text-slate-500">Telefone</p>
                    <p className="font-medium text-slate-900">{employee.telefone || 'Não informado'}</p>
                  </div>
                </div>
                
                {employee.dataNascimento && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="text-sm text-slate-500">Data de Nascimento</p>
                      <p className="font-medium text-slate-900">{formatDate(employee.dataNascimento)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Eventos */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow-lg border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Eventos Participados</h3>
                      <p className="text-sm text-emerald-100">Histórico de escalações</p>
                    </div>
                  </div>
                  <div className="text-white">
                    <span className="text-2xl font-bold">{eventos.length}</span>
                    <p className="text-sm text-emerald-100">Total</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {loadingEventos ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-4"></div>
                    <p className="text-slate-600">Carregando eventos...</p>
                  </div>
                ) : eventos.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">Nenhum evento encontrado</p>
                    <p className="text-sm text-slate-400">Este funcionário ainda não foi escalado para eventos</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {eventos.map((evento, index) => (
                      <div key={evento.contractId || index} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-slate-900">{evento.nomeAniversariante}</h4>
                              <button
                                onClick={() => navigate(`/admin/contratos/${evento.contractId}`)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition-colors"
                              >
                                <Eye className="w-3 h-3" />
                                Ver Contrato
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                              <div>
                                <span className="text-slate-500">Data:</span>
                                <span className="ml-2 font-medium">{formatDate(evento.dataFesta)}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Função:</span>
                                <span className="ml-2 font-medium">{evento.funcao || 'Não especificada'}</span>
                              </div>
                              <div className="flex items-center">
                                <DollarSign className="w-4 h-4 text-emerald-500 mr-1" />
                                <span className="font-medium text-emerald-600">
                                  {formatCurrency(evento.valorPagamento.toString())}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <EditEmployeeModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleEdit}
          employee={employee}
        />
      )}
    </div>
  );
};
